// src/hr/timeoff/labels.js
// Words and tones shared by the Time Off screens. Time is in hours (1 day = 8).
import { daysLabel, daysNumber } from '../format';
import { workdaysBetween } from './dates';

const KEY_TONES = { pto: 'pto', sick: 'sick', personal: 'personal' };

// The short name a type goes by on screen; the locked sick type is "Sick & Safe".
export function typeLabel(type) {
  if (!type) return '';
  if (type.isSick || type.key === 'sick' || type.typeKey === 'sick') return 'Sick & Safe';
  return type.name || type.typeName || '';
}

// Pill props for a type (or anything carrying key / typeKey and color).
export function typeTone(type) {
  const key = type && (type.key || type.typeKey);
  if (key && KEY_TONES[key]) return { tone: KEY_TONES[key] };
  if (type && type.color) return { color: type.color };
  return { tone: 'mute' };
}

export const REQUEST_STATUS = {
  pending_manager: { label: 'Waiting on manager', tone: 'warn' },
  pending_hr: { label: 'Waiting on HR', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  declined: { label: 'Declined', tone: 'bad' },
  cancelled: { label: 'Cancelled', tone: 'mute' }
};

// What a request is waiting on, as the employee sees it. The server names no manager exactly when a
// manager step has fallen to HR (the approver has left or was archived), so that one reads as HR's.
export function requestStatusOf(request) {
  if (request && request.status === 'pending_manager' && !request.managerName) return REQUEST_STATUS.pending_hr;
  return (request && REQUEST_STATUS[request.status]) || null;
}

export const isPending = (request) => request.status === 'pending_manager' || request.status === 'pending_hr';

// Pending requests, and approved ones that have not started, can be withdrawn (the server's rule).
export function isCancellable(request, today) {
  if (!request) return false;
  if (isPending(request)) return true;
  return request.status === 'approved' && !!request.startDate && request.startDate > today;
}

// Client-side estimate of a request's hours: weekdays minus company holidays, times hours per day.
// The server recounts and has the last word.
export function estimateHours(startDate, endDate, hoursPerDay, holidaySet) {
  return workdaysBetween(startDate, endDate, holidaySet).length * hoursPerDay;
}

// "Full day", "Half day", "3 full days" ... for a row without a note.
export function lengthWords(hoursPerDay, totalHours) {
  const half = Number(hoursPerDay) === 4;
  const days = Math.round((Number(totalHours) || 0) / (Number(hoursPerDay) || 8));
  if (days <= 1) return half ? 'Half day' : 'Full day';
  return half ? `${days} half days` : `${days} full days`;
}

// '+3 days', '−1 day' (a real minus sign), '0 days'.
export function signedDays(hours) {
  const n = Number(hours) || 0;
  if (n > 0) return `+${daysLabel(n)}`;
  if (n < 0) return `−${daysLabel(-n)}`;
  return daysLabel(0);
}

export { daysLabel, daysNumber };

export const LEDGER_KINDS = {
  grant: 'Allowance added',
  carryover: 'Carried over',
  usedBeforePortal: 'Used before the portal',
  adjustment: 'Adjusted by HR',
  entry: 'Time off taken',
  entryReversal: 'Time off returned',
  payout: 'Paid out'
};
